import styled, { css, type FlattenInterpolation, type ThemedStyledProps } from 'styled-components'

import * as BREAKPOINT from '../constants/breakpoints'
import * as colors from '../constants/colors'
import * as fonts from '../constants/fonts'
import * as typography from '../constants/neue/typography'

export const _baseStyle = css`
  color: ${colors.PRIMARY_GREY_900};
  font-family: ${fonts.PRIMARY_FONT};
  margin: 0;
  padding: 0;
`

type StyleVariant = {
  fontSize: string
  fontWeight: number
  lineHeight: string
}

interface StyleVariants {
  regular: StyleVariant
  semiBold: StyleVariant
  light: StyleVariant
}

const styles = {
  XS: {
    regular: typography.COPY_XS_REGULAR,
    semiBold: typography.COPY_XS_SEMIBOLD,
  },
  S: {
    regular: typography.COPY_S_REGULAR,
    semiBold: typography.COPY_S_SEMIBOLD,
  },
  M: {
    light: typography.COPY_M_LIGHT,
    semiBold: typography.COPY_M_SEMIBOLD,
  },
  L: {
    light: typography.COPY_L_LIGHT,
    semiBold: typography.COPY_L_SEMIBOLD,
  },
} as const

type StyleSizes = keyof typeof styles

export const Base = styled.p.withConfig({
  shouldForwardProp: (prop) => !['asDesktop'].includes(prop),
})<React.HTMLAttributes<HTMLParagraphElement>>`
  ${_baseStyle}
`

type AsDesktopProps = { asDesktop?: string; breakpoint?: string } // Only 'asDesktop' prop now

const asDesktopStyle: FlattenInterpolation<ThemedStyledProps<AsDesktopProps, any>> = css<AsDesktopProps>`
  @media screen and ${({ breakpoint = BREAKPOINT.TABLET }) => breakpoint} {
    ${({ asDesktop }) => {
      if (!asDesktop) return ''

      const [size, variant] = asDesktop.split('.')

      if (typeof size !== 'string' || typeof variant !== 'string') {
        console.warn(
          `[plume] Invalid asDesktop value: ${asDesktop}.  Expected format: 'Size.Variant' (e.g., 'L.light')`,
        )
        return ''
      }

      if (!size || !variant) {
        console.warn(`[plume] Invalid asDesktop value: ${asDesktop}. Missing size or variant.`)
        return ''
      }

      const styleSize = styles[size as StyleSizes]

      if (!styleSize || !variant) {
        // Check if variant exists within the size
        console.warn(`[plume] Invalid asDesktop value: ${asDesktop}.  Invalid size or variant combination.`)
        return ''
      }

      // @ts-expect-error TS struggles to infer the correct type for the variant
      return (styleSize as StyleVariants)[variant]
    }}
  }
`
type CopyProps = React.HTMLAttributes<HTMLParagraphElement> & AsDesktopProps

export const XS = {
  regular: styled(Base)<CopyProps>`
    ${typography.COPY_XS_REGULAR};
    ${asDesktopStyle};
  `,
  semiBold: styled(Base)<CopyProps>`
    ${typography.COPY_XS_SEMIBOLD};
    ${asDesktopStyle};
  `,
}
XS.regular.displayName = 'S.copy.XS.regular'
XS.semiBold.displayName = 'S.copy.XS.semiBold'

export const S = {
  regular: styled(Base)<CopyProps>`
    ${typography.COPY_S_REGULAR};
    ${asDesktopStyle};
  `,
  semiBold: styled(Base)<CopyProps>`
    ${typography.COPY_S_SEMIBOLD};
    ${asDesktopStyle};
  `,
}
S.regular.displayName = 'S.copy.S.regular'
S.semiBold.displayName = 'S.copy.S.semiBold'

export const M = {
  light: styled(Base)<CopyProps>`
    ${typography.COPY_M_LIGHT};
    ${asDesktopStyle};
  `,
  semiBold: styled(Base)<CopyProps>`
    ${typography.COPY_M_SEMIBOLD};
    ${asDesktopStyle};
  `,
}
M.light.displayName = 'S.copy.M.light'
M.semiBold.displayName = 'S.copy.M.semiBold'

export const L = {
  light: styled(Base)<CopyProps>`
    ${typography.COPY_L_LIGHT};
    ${asDesktopStyle};
  `,
  semiBold: styled(Base)<CopyProps>`
    ${typography.COPY_L_SEMIBOLD};
    ${asDesktopStyle};
  `,
}
L.light.displayName = 'S.copy.L.light'
L.semiBold.displayName = 'S.copy.L.semiBold'
